import { z } from 'zod'

import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'

/**
 * Parent consent to sending school material to the extraction provider
 * (NFR-05, D-15). Implemented by
 * `supabase/migrations/20261107000001_material_processing_consent.sql`.
 *
 * The server decides everything: the family (from the login), the current
 * notice version, the parent role and the parent PIN. The client only shows
 * the notice for the version the server names and sends that version back,
 * so a parent can only agree to the text they were shown.
 */

export const consentStateSchema = z.object({
  policy_version: z.string(),
  status: z.enum(['none', 'granted', 'outdated', 'withdrawn']),
  consented_version: z.string().nullable(),
  granted_at: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
})
export type ConsentState = z.infer<typeof consentStateSchema>

const statusSchema = consentStateSchema.extend({ can_manage: z.boolean() })
export type ConsentStatus = z.infer<typeof statusSchema>

const changeSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), consent: consentStateSchema }),
  z.object({ status: z.literal('policy_version_mismatch'), consent: consentStateSchema }),
  z.object({
    status: z.literal('pin_rejected'),
    verified: z.literal(false),
    locked_until: z.string().nullable(),
    attempts_remaining: z.number().int(),
  }),
])
export type ConsentChange = z.infer<typeof changeSchema>

export interface MaterialConsentApi {
  status(): Promise<ConsentStatus>
  grant(policyVersion: string, parentPin: string): Promise<ConsentChange>
  withdraw(parentPin: string): Promise<ConsentChange>
}

export function createMaterialConsentApi(client: AppSupabaseClient): MaterialConsentApi {
  return {
    status: () => callRpc(client, 'get_material_processing_consent', {}, statusSchema),
    grant: (policyVersion, parentPin) =>
      callRpc(
        client,
        'grant_material_processing_consent',
        { p_policy_version: policyVersion, p_pin: parentPin },
        changeSchema,
      ),
    withdraw: (parentPin) =>
      callRpc(
        client,
        'withdraw_material_processing_consent',
        { p_pin: parentPin },
        changeSchema,
      ),
  }
}
